/**
 * Lightweight coloured debug logger for browser console.
 * Ported from PopshopRN/src/utils/makeDebug.ts
 */
import { type DebugTag, enabledDebuggers, isDebugEnabled } from './debugging';

const assignedColors: Record<string, string> = {};
const debuggers: Record<string, Debugger> = {};

const CSS_COLORS: Record<string, string> = {
  black: '#000',
  red: '#e03131',
  green: '#2f9e44',
  yellow: '#e67700',
  blue: '#1971c2',
  magenta: '#ae3ec9',
  cyan: '#0c8599',
  grey: '#868e96',
  redBright: '#ff6b6b',
  greenBright: '#51cf66',
  yellowBright: '#fcc419',
  blueBright: '#339af0',
  magentaBright: '#cc5de8',
  cyanBright: '#22b8cf',
  orange: '#e8590c',
  pink: '#d6336c',
  teal: '#0ca678',
  indigo: '#4263eb',
  violet: '#7048e8',
  lime: '#66a80f',
  coral: '#e8726d',
  sky: '#4dabf7',
  amber: '#f59f00',
  rose: '#f06595',
  emerald: '#20c997',
  slate: '#5c7cfa',
};

const COLOR_CYCLE = [
  'blue',
  'green',
  'red',
  'teal',
  'orange',
  'violet',
  'cyan',
  'pink',
  'indigo',
  'lime',
  'magenta',
  'sky',
  'amber',
  'emerald',
  'coral',
  'slate',
  'redBright',
  'greenBright',
  'yellowBright',
  'blueBright',
  'magentaBright',
  'cyanBright',
  'rose',
  'yellow',
];

let nextColorIndex = 0;

function pickColor(name: string): string {
  let color = assignedColors[name];
  if (!color) {
    color = COLOR_CYCLE[nextColorIndex++ % COLOR_CYCLE.length];
    assignedColors[name] = color;
  }
  return color;
}

// ─── Types ─────────────────────────────────────────────────────────────────

export type Debugger = {
  (message: string, ...args: unknown[]): void;
  success: (message: string, ...args: unknown[]) => void;
  error: (message: string, ...args: unknown[]) => void;
  warn: (message: string, ...args: unknown[]) => void;
  info: (message: string, ...args: unknown[]) => void;
  verbose: (message: string, ...args: unknown[]) => void;
  group: (message: string, ...args: unknown[]) => void;
  groupEnd: () => void;
  time: (label?: string) => void;
  timeEnd: (label?: string) => void;
};

// ─── Format helpers ────────────────────────────────────────────────────────

function formatMessage(message: string, args: unknown[]): [string, unknown[]] {
  let index = 0;
  const formatted = message.replace(/%[a-zA-Z%]/g, match => {
    if (match === '%%') return '%';
    let arg = args[index++];

    if (typeof arg === 'function') arg = arg();
    if (arg === null) return 'null';
    if (arg === undefined) return 'undefined';
    if (match === '%o') return JSON.stringify(arg);
    if (match === '%O') return JSON.stringify(arg, null, 2);
    if (match === '%d' || match === '%f') return String(Number(arg));
    return String(arg);
  });

  const remaining = args
    .slice(index)
    .map(arg => (typeof arg === 'function' ? arg() : arg));

  return [formatted, remaining];
}

// ─── Factory ───────────────────────────────────────────────────────────────

const isDev =
  typeof process !== 'undefined' ? process.env.NODE_ENV !== 'production' : true;

function factory(
  enabled: boolean,
  consoleFn: (...args: unknown[]) => void,
  name: string,
  nameColor: string,
  messageColor?: string,
): (message: string, ...args: unknown[]) => void {
  const nameCss = CSS_COLORS[nameColor] ?? CSS_COLORS.grey;
  const msgCss = CSS_COLORS[messageColor ?? nameColor] ?? nameCss;

  return (message: string, ...args: unknown[]) => {
    if (!enabled || !isDev) return;

    const [formatted, remaining] = formatMessage(message, args);

    consoleFn(
      `%c[${name}]%c ${formatted}`,
      `color: ${nameCss}; font-weight: bold`,
      `color: ${msgCss}`,
      ...remaining,
    );
  };
}

// ─── Public API ────────────────────────────────────────────────────────────

/**
 * Create a named debug logger.
 *
 * @param name - Debug tag. Known tags (registered in `enabledDebuggers`)
 *   pick up the registry flag automatically; ad-hoc / feature-scoped
 *   names are allowed too as long as the caller passes `enabled`.
 *   `(string & {})` keeps autocomplete for the known tags while still
 *   accepting arbitrary strings.
 * @param enabled - Explicit override. Defaults to the registry flag
 *   when `name` is a registered tag.
 * @param verbose - Enable the `.verbose()` channel.
 */
export function makeDebug(
  name: DebugTag | (string & {}),
  enabled?: boolean,
  verbose = false,
): Debugger {
  if (debuggers[name]) {
    return debuggers[name];
  }

  const isActive =
    (enabled ??
      (name in enabledDebuggers && isDebugEnabled(name as DebugTag))) &&
    isDev;
  const nameColor = pickColor(name);

  const debug = factory(isActive, console.debug, name, nameColor) as Debugger;

  debug.success = factory(isActive, console.info, name, nameColor, 'green');
  debug.info = factory(isActive, console.info, name, nameColor, 'cyan');
  debug.warn = factory(isActive, console.warn, name, nameColor, 'yellow');
  debug.error = factory(true, console.error, name, nameColor, 'red');
  debug.verbose = factory(
    isActive && verbose,
    console.debug,
    name,
    nameColor,
    'blue',
  );
  debug.group = factory(isActive, console.group, name, nameColor);
  debug.groupEnd = () => {
    if (!isActive || !isDev) return;
    console.groupEnd();
  };

  const timerLabel = (label?: string) => `[${name}] ${label ?? 'default'}`;
  debug.time = (label?: string) => {
    if (!isActive || !isDev) return;
    console.time(timerLabel(label));
  };
  debug.timeEnd = (label?: string) => {
    if (!isActive || !isDev) return;
    console.timeEnd(timerLabel(label));
  };

  debuggers[name] = debug;
  return debug;
}
